/** Шапка свежести «WB: регионы»: сборка (когда, сколько назад), даты «сейчас» по кабинету (FBO/
 *  спрос, FBS), проверки fail/warn; всплывашка всех источников кабинета (`data_through`).
 *  Плашки (`BuildBanners`): «сборка не прошла» (`build_late`) — жёлтая, цифры показываются;
 *  «данные другой сборки» (`other_build`) — жёлтая с причинами; «ответы разных сборок»; fail
 *  проверок > 0 — красная строка-ссылка на вкладку «Проверки». */
import React from "react";
import { Link } from "react-router-dom";

import type { WbLegal, WbMeta, WbRegionsState } from "@/api/wbRegions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtWhen, timeAgo } from "../ozon-analytics/format";
import { BuildBanners } from "./BuildBanners";

/** Источники кабинета по порядку (ключ `data_through` → подпись). Ключи вне списка тоже
 *  показываются (своим именем) — не прячем то, что отдала витрина. */
const SOURCES: { key: string; label: string }[] = [
  { key: "fbo_stock_date", label: "FBO-оценка и спрос (день хранения)" },
  { key: "fbs_snapshot_date", label: "FBS-снимок" },
  { key: "fbs_loaded_at", label: "FBS загружен" },
  { key: "storage_date", label: "Отчёт хранения" },
  { key: "remains_date", label: "Снимок remains (заморожено)" },
  { key: "stocks_report_date", label: "Отчёт об остатках" },
  { key: "orders_date", label: "Заказы" },
  { key: "order_feed_date", label: "Лента заказов" },
  { key: "order_feed_loaded_at", label: "Лента заказов загружена" },
  { key: "logistics_date", label: "Логистика реализации" },
];
const NOT_SOURCES = new Set(["legal_name", "active", "checks"]);

function SourcesPopover({ legals }: { legals: WbLegal[] }): React.ReactElement {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full border px-2 py-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          data-testid="sources-toggle"
        >
          по источникам
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[min(560px,90vw)] p-3 text-xs">
        <div className="space-y-3" data-testid="sources">
          {legals.map((l) => {
            const extra = Object.keys(l).filter(
              (k) => !NOT_SOURCES.has(k) && !SOURCES.some((s) => s.key === k),
            );
            const rows = [...SOURCES, ...extra.map((k) => ({ key: k, label: k }))];
            return (
              <section key={l.legal_name} className="space-y-1" data-testid="sources-legal">
                <h4 className="font-medium">{l.legal_name}</h4>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  {rows.map((s) => {
                    const v = l[s.key];
                    return (
                      <React.Fragment key={s.key}>
                        <dt className="text-muted-foreground">{s.label}</dt>
                        <dd className="tabular-nums">
                          {v === undefined ? (
                            <span
                              className="text-amber-700 dark:text-amber-400"
                              title={`ключа ${s.key} нет в data_through витрины`}
                              data-testid="source-missing-key"
                            >
                              нет в ответе витрины
                            </span>
                          ) : typeof v === "string" || v === null ? (
                            fmtWhen(v)
                          ) : (
                            JSON.stringify(v)
                          )}
                        </dd>
                      </React.Fragment>
                    );
                  })}
                </dl>
              </section>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function FreshnessBar({
  meta,
  legals,
  states,
  mixed,
  checksSearch,
}: {
  meta: WbMeta;
  /** кабинеты экрана: один или все активные (`all`) — даты по каждому */
  legals: WbLegal[];
  /** свежесть показанных ответов (meta и данные вкладки) — плашки по любому из них */
  states: WbRegionsState[];
  /** сборки показанных ответов, если их больше одной (`useBuildSync`) */
  mixed: string[] | null;
  /** адрес вкладки «Проверки» (query string) — ссылка из строки fail */
  checksSearch: string;
}): React.ReactElement {
  const failing = legals.filter((l) => l.checks.fail > 0);
  return (
    <div className="space-y-2" data-testid="freshness">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="rounded-full border px-2 py-0.5 text-muted-foreground" data-testid="build">
          Сборка {fmtWhen(meta.built_at)} ({timeAgo(meta.built_at)})
        </span>
        {legals.map((l) => (
          <span
            key={l.legal_name}
            className="rounded-full border px-2 py-0.5 text-muted-foreground"
            data-testid="legal-dates"
          >
            {legals.length > 1 && <b className="font-medium text-foreground">{l.legal_name}: </b>}
            FBO/спрос на {fmtWhen(l.fbo_stock_date ?? null)} · FBS на{" "}
            {fmtWhen(l.fbs_snapshot_date ?? null)} · проверки: {l.checks.fail} fail,{" "}
            {l.checks.warn} warn
          </span>
        ))}
        <SourcesPopover legals={legals} />
      </div>
      <BuildBanners states={states} mixed={mixed} />
      {failing.length > 0 && (
        <div
          role="alert"
          data-testid="checks-fail"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          <Link to={{ search: `?${checksSearch}` }} className="underline hover:no-underline" data-testid="checks-fail-link">
            Проверки сходимости не прошли:{" "}
            {failing.map((l) => `${l.legal_name} — ${l.checks.fail} fail`).join(", ")}
          </Link>
          . Цифры могут не сходиться с данными WB; подробности — во вкладке «Проверки».
        </div>
      )}
    </div>
  );
}
